import React from 'react'
import { faFileUpload, faSquareCheck } from '@fortawesome/free-solid-svg-icons'
import StepBar from 'app/common/StepBar'
import {
  uploadInventory,
  validateProducts,
} from 'client/utils/api-routes-inventory'
import {
  $DCprimaryColor,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcText,
  Link,
  PixelCheckboxGroup,
  PixelFactoryContext,
} from 'pixel-base'
import styled from 'styled-components'
import * as XLSX from 'xlsx'

import InventoryValidationModal, {
  IUploadResults,
} from './InventoryValidationModal'
import { IPixelFactory } from '../../../../../../decl.d'

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: flex-end;
  align-content: flex-end;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  .full-width {
    flex-grow: 1;
  }
`

const RequiredKaysContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  font-size: 12px;
  padding: 5px 15px;
  border-radius: 5px;
  background-color: rgb(255, 255, 255);
  box-shadow: rgb(0 0 0 / 15%) 0px 0px 5px;
  gap: 5px;
`

interface Data {
  warehouse_id: string
  inventory_list: File[]
  file_name: string
  update_type: string
  notes: string
}
interface IInventoryUpdateResponse {
  message: string
  successful_updates: {
    product_id: number
    product_sku: string
    status: string // could be narrowed to 'success' if that's always the value
  }[]
  errors: string[] // Can be refined if you know the structure of error objects
  details?: any[] // Add missing details property
  error?: string // Add missing error property
}

const InventoryUploadModal = (props) => {
  const { showPageLoader, hidePageLoader, showSuccess, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [activeStep] = React.useState('upload')
  const [error, setError] = React.useState({
    error: '',
    field: '',
  })
  const [data, setData] = React.useState<Data>({
    inventory_list: [],
    warehouse_id: '',
    file_name: '',
    update_type: '',
    notes: '',
  })

  // Validation states
  const [showValidationModal, setShowValidationModal] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [uploadResults, setUploadResults] =
    React.useState<IUploadResults | null>(null)
  const [validatedFile, setValidatedFile] = React.useState<File | null>(null)

  // Get tenant info for store_id
  const tenantInfo = JSON.parse(localStorage.getItem('tenant_info') || '{}')
  const processUploadedFile = (file: File) => {
    const isExcel =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel'

    if (file.type === 'text/csv') {
      // Process CSV file
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string
          const lines = content.split('\n')
          const products: { product_sku: string; stock_count: number }[] = []

          // Parse header to find column indices
          const headerLine = lines[0]?.trim()
          if (!headerLine) {
            showFailure('Invalid CSV file: missing header row')
            return
          }

          const headers = headerLine
            .split(',')
            .map((h) => h.trim().toLowerCase())
          const skuIndex = headers.findIndex(
            (h) => h === 'product_sku' || h === 'sku' || h === 'product sku',
          )
          const stockIndex = headers.findIndex(
            (h) =>
              h === 'stock_count' || h === 'quantity' || h === 'stock count',
          )

          if (skuIndex === -1) {
            showFailure('Invalid CSV file: missing product_sku column')
            return
          }

          if (stockIndex === -1) {
            showFailure('Invalid CSV file: missing stock_count column')
            return
          }

          // Process data rows
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim()
            if (line) {
              const columns = line.split(',')
              const productSku = columns[skuIndex]?.trim()
              const stockCount = columns[stockIndex]?.trim()

              if (productSku && stockCount) {
                products.push({
                  product_sku: productSku,
                  stock_count: parseInt(stockCount) || 0,
                })
              }
            }
          }

          if (products.length === 0) {
            showFailure('No valid products found in the CSV file')
            return
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing CSV file')
          console.error('CSV processing error:', error)
        }
      }
      reader.readAsText(file)
    } else if (isExcel) {
      // Process Excel file
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<
            string,
            any
          >[]

          if (jsonData.length === 0) {
            showFailure('The uploaded Excel file is empty.')
            return
          }

          const products: { product_sku: string; stock_count: number }[] = []

          jsonData.forEach((row: Record<string, any>) => {
            // Handle different possible column names
            const productSkuKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'product_sku' ||
                key.toLowerCase() === 'sku' ||
                key.toLowerCase() === 'product sku',
            )

            const stockCountKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'stock_count' ||
                key.toLowerCase() === 'stock count' ||
                key.toLowerCase() === 'quantity',
            )

            if (productSkuKey && stockCountKey) {
              const productSku = row[productSkuKey]?.toString().trim()
              const stockCount = row[stockCountKey]?.toString().trim()

              if (productSku && stockCount) {
                products.push({
                  product_sku: productSku,
                  stock_count: parseInt(stockCount) || 0,
                })
              }
            }
          })

          if (products.length === 0) {
            showFailure('No valid products found in the Excel file')
            return
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing file')
          console.error('File processing error:', error)
        }
      }
      reader.readAsArrayBuffer(file)
    } else {
      showFailure('Unsupported file type. Please upload a CSV or Excel file.')
    }
  }

  const validateProductsFromAPI = (
    products: { product_sku: string; stock_count: number }[],
  ) => {
    const productSkus = products.map((p) => p.product_sku)
    const storeId = tenantInfo?.store_id || 1

    setUploadProgress(25)
    showPageLoader()
    validateProducts({
      product_skus: productSkus,
      store_id: storeId,
    }).subscribe(
      (res) => {
        const response = res as {
          response: {
            data: any[]
            valid_products: any[]
            _meta: {
              found_count: number
              error_count: number
              success_rate: number
            }
          }
        }
        hidePageLoader()
        setUploadProgress(75)

        if (response.response?.data) {
          const validationResult = response.response
          const validProducts = validationResult.valid_products || []
          const allProducts = validationResult.data || []

          const successCount = validationResult._meta?.found_count || 0
          const errorCount = validationResult._meta?.error_count || 0
          const successRate = validationResult._meta?.success_rate || 0

          const invalidProducts = allProducts.filter(
            (p) => 'status' in p && p.status === 'error',
          )

          setUploadResults({
            validProducts,
            invalidProducts,
            successCount,
            errorCount,
            successRate,
          })

          if (successCount > 0) {
            showSuccess(
              `Successfully validated ${successCount} products (${successRate.toFixed(
                1,
              )}% success rate)`,
            )
          } else {
            showFailure('No valid products found in the uploaded file')
          }

          setUploadProgress(100)
        } else {
          showFailure('Error validating products')
          setUploadProgress(0)
        }
      },
      (error) => {
        hidePageLoader()
        showFailure('Error validating products')
        setUploadProgress(0)
        console.error('Validation error:', error)
      },
    )
  }

  const handleProceedWithUpload = () => {
    setShowValidationModal(false)
    uploadBulkInventory()
  }

  const handleCancelValidation = () => {
    setShowValidationModal(false)
    setUploadResults(null)
    setUploadProgress(0)
    setValidatedFile(null)
  }

  const uploadBulkInventory = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (
      error.field === 'inventory_list' &&
      error.error === 'Please upload a xlsx or csv file'
    ) {
      return
    }
    showPageLoader()
    uploadInventory(data).subscribe((res) => {
      const response = res.response as IInventoryUpdateResponse
      hidePageLoader()
      if (response.message) {
        showSuccess(response.message)
        props.handleClose('success')
      } else {
        if (response.details && Array.isArray(response.details)) {
          const detailMsg = response.details
            .map(
              (item: any) =>
                `Product ID: ${item.product_id}\n${item.errors.join(', ')}`,
            )
            .join('\n\n')
          showFailure(`${response.error || 'Error'}\n${detailMsg}`)
        } else {
          showFailure(response.error || 'Unknown error')
        }
      }
    })
  }
  const Steps = [
    {
      icon: faFileUpload,
      active: false,
      title: 'upload',
    },
    {
      icon: faSquareCheck,
      active: false,
      title: 'product',
    },
  ]
  const handleDownloadTemplate = () => {
    // Create template data
    const templateData = [
      { product_id: 1, product_sku: 'SKU001', stock_count: 10 },
      { product_id: 2, product_sku: 'SKU002', stock_count: 5 },
      { product_id: 3, product_sku: 'SKU003', stock_count: 20 },
    ]

    // Create Excel file
    const ws = XLSX.utils.json_to_sheet(templateData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')

    // Download Excel file
    XLSX.writeFile(wb, 'inventory_template.xlsx')
  }

  return (
    <form onSubmit={uploadBulkInventory}>
      <StepBar size="sm" Steps={Steps} activeStep={activeStep} />
      <div style={{ fontSize: '14px', padding: '10px 15px' }}>
        Product stock is updated based on match by <b>SKU</b> or{' '}
        <b>Product_ID</b>. Update your Stock with two easy steps. <br />
        {activeStep === 'upload' ? (
          <>
            <strong>Step I:</strong> Upload your inventory according to this{' '}
            <Link
              decoration
              onClick={() => handleDownloadTemplate()}
              style={{ fontSize: '14px', color: $DCprimaryColor }}
            >
              template
            </Link>{' '}
            (supports CSV, XLSX, and XLS files) and select the warehouse and
            upload the stock.
          </>
        ) : (
          <div>
            <strong>Step II:</strong> Review the unmatched products and finish
          </div>
        )}
      </div>
      {activeStep === 'upload' ? (
        <DcFlexBox height="auto" flexDirection="column" width="100%" gap="10px">
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',
                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Select File
            </DcText>
            <DcInput
              error={
                error.field === 'inventory_list' && error.error !== ''
                  ? error.error
                  : undefined
              }
              required
              type={'file'}
              accept=".csv,.xlsx,.xls"
              name={'Select File'}
              placeholder={'Select File'}
              onChange={(e) => {
                if (
                  e.target.files[0].type ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                  e.target.files[0].type === 'application/vnd.ms-excel' ||
                  e.target.files[0].type === 'text/csv'
                ) {
                  const file = e.target.files[0]
                  setData({
                    ...data,
                    inventory_list: file,
                    file_name: file.name,
                  })
                  setError({
                    error: '',
                    field: 'inventory_list',
                  })

                  // Start validation process
                  setValidatedFile(file)
                  setShowValidationModal(true)
                  setUploadProgress(0)
                  setUploadResults(null)
                  processUploadedFile(file)
                } else {
                  setError({
                    error: 'Please upload a xlsx, xls or csv file',
                    field: 'inventory_list',
                  })
                }
              }}
            />
            {data.file_name && (
              <DcFlexBox
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#6c757d',
                }}
              >
                <DcText textSize="12px" style={{ color: '#6c757d' }}>
                  📄 Selected file: {data.file_name}
                </DcText>
              </DcFlexBox>
            )}
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',
                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Warehouse
            </DcText>
            <DcDropDown
              error={
                error.field === 'warehouse_id' && error.error !== ''
                  ? error.error
                  : undefined
              }
              required
              placeholder={'Select Warehouse'}
              options={[
                { name: 'Select Warehouse', wh_id: '' },
                ...props.warehousesList,
              ].map((data: any) => {
                return {
                  label: data.name,
                  value: data.wh_id,
                }
              })}
              value={data.warehouse_id}
              onChange={(e) => {
                setData({
                  ...data,
                  warehouse_id: e.target.value,
                })
              }}
              customLabel={''}
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <PixelCheckboxGroup
              className="checkbox-group"
              label="Update Type"
              options={[
                {
                  label: 'Add',
                  value: 'add',
                  type: 'radio',
                  checked: data.update_type === 'add',
                },
                {
                  label: 'Set',
                  value: 'set',
                  type: 'radio',
                  checked: data.update_type === 'set',
                },
              ]}
              onChange={(e) => {
                setData({ ...data, update_type: e.target.value })
              }}
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',
                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Notes
            </DcText>
            <DcInput
              error={
                error.field === 'notes' && error.error !== ''
                  ? error.error
                  : undefined
              }
              required
              type={'text'}
              as={'textarea'}
              name={'Notes'}
              placeholder={'Notes'}
              value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
            />
          </DcDiv>
          <DcFlexBox justifyContent="flex-end" gap="5px" height="auto">
            <DcButton
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                props.handleClose()
              }}
            >
              Cancel
            </DcButton>{' '}
            <DcButton
              type="submit"
              size="sm"
              disabled={
                !data.inventory_list || !data.warehouse_id || !data.update_type
              }
            >
              Upload
            </DcButton>
          </DcFlexBox>
        </DcFlexBox>
      ) : (
        <FormGroup>
          <RequiredKaysContainer>
            These products are not available in the system. Please add them
            first to update the stock.
            <br />
            <div>
              <b>Product name:</b> Iphone 13, Iphone 12, Iphone 11, Iphone 10,
              Iphone 9, Iphone 8, Iphone 7, Iphone 6, Iphone 5, Iphone 4, Iphone
              3, Iphone 2, Iphone 1,
            </div>
          </RequiredKaysContainer>
          <div>
            {' '}
            <DcButton onClick={() => props.handleClose()} size="sm">
              Done
            </DcButton>
          </div>
        </FormGroup>
      )}

      {/* Validation Modal */}
      <InventoryValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        uploadedFile={validatedFile}
        uploadProgress={uploadProgress}
        uploadResults={uploadResults}
        onProceedWithUpload={handleProceedWithUpload}
        onCancel={handleCancelValidation}
      />
    </form>
  )
}

export default InventoryUploadModal
